import { clsx } from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { DealerComparisonPricesPayload } from "../../queries/useGetDealerComparisonPricesByTireSize";
import { PricingAnalysisFormState } from "../../stores/slices/pricingAnalysisSlice";
import { PriceDisplay } from "../../ui/PriceDisplay/PriceDisplay";
import { TableHeader } from "../ColumnSort/TableHeader";
import { NoData } from "../NoData/NoData";
import { useFilters } from "./useFilters";

type PricingAnalysisTableProps = {
  data: DealerComparisonPricesPayload;
  state: PricingAnalysisFormState;
};

export const PricingAnalysisTable: FunctionComponent<
  PricingAnalysisTableProps
> = ({ data, state }) => {
  const { t } = useTranslation();
  const filteredData = useFilters(data, state);

  if (!filteredData.length) {
    return (
      <NoData className="max-w-[16rem] mx-auto">
        {t("pricingAnalysis.noData")}
      </NoData>
    );
  }

  return (
    <div className=" overflow-x-auto">
      <table className="border-collapse table-auto w-full text-xs">
        <thead>
          <tr className="border-b-2 px-1 md:px-2">
            <TableHeader
              className="text-left"
              field="brand"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-left"
              field="name"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="partNumber"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-left"
              field="tireSize"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="costPrice"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="yourPrice"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="minPrice"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="averagePrice"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="quantityBooked"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-right"
              field="quantitySold"
              tableName="pricingAnalysis"
            />
            <TableHeader
              className="text-left"
              field="summary"
              tableName="pricingAnalysis"
            />
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => {
            return (
              <tr
                className={clsx("border-b", item.isBestPrice && "bg-lime-200")}
                key={item.key}
              >
                <td className="px-1 md:px-2 md:py-2">{item.brand}</td>
                <td className="px-1 md:px-2 md:py-2">{item.name}</td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  {item.partNumber}
                </td>
                <td className="px-1 md:px-2 md:py-2">{item.tireSize}</td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  <PriceDisplay>{item.costPrice}</PriceDisplay>
                </td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  <PriceDisplay>{item.yourPrice}</PriceDisplay>
                </td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  <PriceDisplay>{item.minPrice}</PriceDisplay>
                </td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  <PriceDisplay>{item.averagePrice}</PriceDisplay>
                </td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  {item.quantityBooked}
                </td>
                <td className="text-right px-1 md:px-2 md:py-2">
                  {item.quantitySold}
                </td>
                <td className="px-1 md:px-2 md:py-2">{item.summary}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
